// @flow

import * as React from 'react'
import styled from 'styled-components'

import type { ConnectedBrand } from '../../../types'
import { WebshopDesignPageRendererContext } from './shared'
import WebshopGridItemsRenderer from './WebshopGridItemsRenderer'

type WebshopDesignPageBodyRendererProps = {
  brand: ConnectedBrand,
  editedGridItemId?: string | null,
  gridItems: Array<Object>,
  outsideProps?: Object,
}

const WebshopDesignPageBodyRenderer = ({
  brand,
  editedGridItemId,
  gridItems,
  outsideProps,
}: WebshopDesignPageBodyRendererProps) => {
  const { setHeaderBackdropGridItems } = React.useContext(
    WebshopDesignPageRendererContext
  )

  // We extract the header backdrop grid items, dont render them here and just
  // add them to the headerBackdropGridItems context so the header renderer
  // can render them under the header there
  const { headerBackdropGridItems, gridItemsWithoutHeaderBackdrops } =
    React.useMemo(() => extractHeaderBackdropGridItems(gridItems), [gridItems])

  React.useEffect(() => {
    setHeaderBackdropGridItems(headerBackdropGridItems)
    return () => {
      setHeaderBackdropGridItems([])
    }
  }, [headerBackdropGridItems, setHeaderBackdropGridItems])

  return (
    <WebshopGridItemsRenderer
      brand={brand}
      gridItems={gridItemsWithoutHeaderBackdrops}
      editedGridItemId={editedGridItemId}
      outsideProps={outsideProps}
    />
  )
}

export default WebshopDesignPageBodyRenderer

const extractHeaderBackdropGridItems = (gridItems: Array<Object>) => {
  const headerBackdropGridItems = []

  gridItems.forEach(gridItem => {
    if (gridItem.type === 'header_backdrop') {
      headerBackdropGridItems.push(gridItem)

      headerBackdropGridItems.push(
        ...getGridItemDescendants(gridItems, gridItem)
      )
    }
  })

  const headerBackdropGridItemIds = headerBackdropGridItems.map(gi => gi.id)
  const gridItemsWithoutHeaderBackdrops = gridItems.filter(
    gi => !headerBackdropGridItemIds.includes(gi.id)
  )

  return {
    headerBackdropGridItems,
    gridItemsWithoutHeaderBackdrops,
  }
}

const getGridItemDescendants = (
  allGridItems: Array<Object>,
  gridItem: Object
): Array<Object> => {
  const descendantGridItems = []

  const directDescendantGridItems = allGridItems.filter(
    gi => gi.parent_grid_item_id === gridItem.id
  )

  directDescendantGridItems.forEach(directDescendantGridItem => {
    descendantGridItems.push(directDescendantGridItem)

    descendantGridItems.push(
      ...getGridItemDescendants(allGridItems, directDescendantGridItem)
    )
  })

  return descendantGridItems
}
